import { useState } from 'react';
import { Dropdown } from '@shared/components/ui/dropdown/Dropdown';
import { DropdownItem } from '@shared/components/ui/dropdown/DropdownItem';
import { Button } from '@shared/components/ui/button/Button';
import { ButtonLink } from '@shared/components/ui/button-link/ButtonLink';

interface AppNotification {
    id: string;
    avatar: string;
    status: 'online' | 'offline';
    name: string;
    action: string;
    subject: string;
    category: string;
    time: string;
}

const EXAMPLE_NOTIFICATIONS: AppNotification[] = [
    {
        id: '1',
        avatar: '/images/user/user-02.jpg',
        status: 'online',
        name: 'Terry Franci',
        action: 'requests permission to change',
        subject: 'Project - Nganter App',
        category: 'Project',
        time: '5 min ago',
    },
    {
        id: '2',
        avatar: '/images/user/user-03.jpg',
        status: 'online',
        name: 'Alena Franci',
        action: 'requests permission to change',
        subject: 'Project - Nganter App',
        category: 'Project',
        time: '8 min ago',
    },
    {
        id: '3',
        avatar: '/images/user/user-04.jpg',
        status: 'online',
        name: 'Jocelyn Kenter',
        action: 'requests permission to change',
        subject: 'Project - Nganter App',
        category: 'Project',
        time: '15 min ago',
    },
    {
        id: '4',
        avatar: '/images/user/user-05.jpg',
        status: 'offline',
        name: 'Brandon Philips',
        action: 'requests permission to change',
        subject: 'Project - Nganter App',
        category: 'Project',
        time: '1 hr ago',
    },
    {
        id: '5',
        avatar: '/images/user/user-02.jpg',
        status: 'online',
        name: 'Terry Franci',
        action: 'requests permission to change',
        subject: 'Project - Nganter App',
        category: 'Project',
        time: '5 min ago',
    },
    {
        id: '6',
        avatar: '/images/user/user-03.jpg',
        status: 'online',
        name: 'Alena Franci',
        action: 'requests permission to change',
        subject: 'Project - Nganter App',
        category: 'Project',
        time: '8 min ago',
    },
    {
        id: '7',
        avatar: '/images/user/user-04.jpg',
        status: 'online',
        name: 'Jocelyn Kenter',
        action: 'requests permission to change',
        subject: 'Project - Nganter App',
        category: 'Project',
        time: '15 min ago',
    },
    {
        id: '8',
        avatar: '/images/user/user-05.jpg',
        status: 'offline',
        name: 'Brandon Philips',
        action: 'requests permission to change',
        subject: 'Project - Nganter App',
        category: 'Project',
        time: '1 hr ago',
    },
];

const STATUS_DOT_COLOUR: Record<AppNotification['status'], string> = {
    online: 'bg-mp-success',
    offline: 'bg-mp-error',
};

const BellIcon = () => (
    <svg
        className="fill-current"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.75 2.29248C10.75 1.87827 10.4143 1.54248 10 1.54248C9.58583 1.54248 9.25004 1.87827 9.25004 2.29248V2.83613C6.08266 3.20733 3.62504 5.9004 3.62504 9.16748V14.4591H3.33337C2.91916 14.4591 2.58337 14.7949 2.58337 15.2091C2.58337 15.6234 2.91916 15.9591 3.33337 15.9591H4.37504H15.625H16.6667C17.0809 15.9591 17.4167 15.6234 17.4167 15.2091C17.4167 14.7949 17.0809 14.4591 16.6667 14.4591H16.375V9.16748C16.375 5.9004 13.9174 3.20733 10.75 2.83613V2.29248ZM14.875 14.4591V9.16748C14.875 6.47509 12.6924 4.29248 10 4.29248C7.30765 4.29248 5.12504 6.47509 5.12504 9.16748V14.4591H14.875ZM8.00004 17.7085C8.00004 18.1228 8.33583 18.4585 8.75004 18.4585H11.25C11.6643 18.4585 12 18.1228 12 17.7085C12 17.2943 11.6643 16.9585 11.25 16.9585H8.75004C8.33583 16.9585 8.00004 17.2943 8.00004 17.7085Z"
            fill="currentColor"
        />
    </svg>
);

const CloseIcon = () => (
    <svg
        className="fill-current"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
            fill="currentColor"
        />
    </svg>
);

const NotificationItem = ({
    notification,
    onClick,
}: {
    notification: AppNotification;
    onClick: () => void;
}) => (
    <DropdownItem
        onItemClick={onClick}
        className="flex gap-3 rounded-lg border-b border-mp-border p-3 px-4.5 py-3 hover:bg-mp-background"
    >
        <span className="relative block w-full h-10 rounded-full z-1 max-w-10">
            <img
                width={40}
                height={40}
                src={notification.avatar}
                alt="User"
                className="w-full overflow-hidden rounded-full"
            />
            <span
                className={`absolute bottom-0 right-0 z-10 h-2.5 w-full max-w-2.5 rounded-full border-[1.5px] border-mp-surface ${STATUS_DOT_COLOUR[notification.status]}`}
            ></span>
        </span>

        <span className="block">
            <span className="mb-1.5 block space-x-1 text-theme-sm text-mp-muted">
                <span className="font-medium text-mp-foreground">{notification.name}</span>
                <span>{notification.action}</span>
                <span className="font-medium text-mp-foreground">{notification.subject}</span>
            </span>

            <span className="flex items-center gap-2 text-mp-muted text-theme-xs">
                <span>{notification.category}</span>
                <span className="w-1 h-1 bg-mp-muted rounded-full"></span>
                <span>{notification.time}</span>
            </span>
        </span>
    </DropdownItem>
);

export default function NotificationDropdown() {
    const [isOpen, setIsOpen] = useState(false);
    const [notifying, setNotifying] = useState(true);

    function toggleDropdown() {
        setIsOpen(!isOpen);
    }

    function closeDropdown() {
        setIsOpen(false);
    }

    const handleClick = () => {
        toggleDropdown();
        setNotifying(false);
    };
    return (
        <div className="relative">
            <Button
                type="button"
                variant="outline"
                size="none"
                ariaLabel="Notifications"
                onClick={handleClick}
                className="dropdown-toggle relative flex h-11 w-11 rounded-full"
            >
                <span
                    className={`absolute right-0 top-0.5 z-10 h-2 w-2 rounded-full bg-mp-warning-solid ${
                        !notifying ? 'hidden' : 'flex'
                    }`}
                >
                    <span className="absolute inline-flex w-full h-full bg-mp-warning-solid rounded-full opacity-75 animate-ping"></span>
                </span>
                <BellIcon />
            </Button>
            <Dropdown
                isOpen={isOpen}
                onClose={closeDropdown}
                className="absolute -right-[240px] mt-[17px] flex h-[480px] w-[350px] flex-col rounded-2xl border border-mp-border bg-mp-surface p-3 shadow-theme-lg sm:w-[361px] lg:right-0"
            >
                <div className="flex items-center justify-between pb-3 mb-3 border-b border-mp-border">
                    <h5 className="text-lg font-semibold text-mp-foreground">Notification</h5>
                    <Button
                        type="button"
                        variant="clear"
                        size="none"
                        ariaLabel="Close notifications"
                        onClick={toggleDropdown}
                        className="text-mp-muted hover:text-mp-foreground"
                        startIcon={<CloseIcon />}
                    />
                </div>
                <ul className="flex flex-col h-auto overflow-y-auto custom-scrollbar">
                    {EXAMPLE_NOTIFICATIONS.map((notification) => (
                        <li key={notification.id}>
                            <NotificationItem notification={notification} onClick={closeDropdown} />
                        </li>
                    ))}
                </ul>
                <ButtonLink to="/" variant="outline" size="sm" className="mt-3 w-full">
                    View All Notifications
                </ButtonLink>
            </Dropdown>
        </div>
    );
}
